import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Slide,
  CircularProgress,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EventNoteIcon from "@mui/icons-material/EventNote";
import { fetchData, postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const FONT = "'Inter', 'Roboto', sans-serif";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const STATUS_CONFIG = {
  Pending:  { color: "#ff9800", bg: "#fff3e0" },
  Approved: { color: "#4caf50", bg: "#e8f5e9" },
  Rejected: { color: "#f44336", bg: "#ffebee" },
};

const TYPE_CONFIG = {
  Regular:   { color: "#2196f3", bg: "#e3f2fd" },
  Mandatory: { color: "#336599", bg: "#f3e5f5" },
};

const REASON_OPTIONS = ["Sick", "Personal", "Family Emergency", "Vacation", "Other"];

const fallbackData = [
  {
    id: "LV001",
    fromDate: "2026-01-15",
    toDate: "2026-01-17",
    reason: "Sick",
    status: "Approved",
    type: "Regular",
    totalDays: 3,
    appliedOn: "2026-01-12",
  },
  {
    id: "LV002",
    fromDate: "2026-02-20",
    toDate: "2026-02-20",
    reason: "Personal",
    status: "Pending",
    type: "Regular",
    totalDays: 1,
    appliedOn: "2026-02-18",
  },
  {
    id: "LV003",
    fromDate: "2025-12-10",
    toDate: "2025-12-13",
    reason: "Family Emergency",
    status: "Approved",
    type: "Mandatory",
    totalDays: 4,
    appliedOn: "2025-12-08",
  },
  {
    id: "LV004",
    fromDate: "2026-03-05",
    toDate: "2026-03-06",
    reason: "Personal",
    status: "Rejected",
    type: "Regular",
    totalDays: 2,
    appliedOn: "2026-03-01",
  },
  {
    id: "LV005",
    fromDate: "2026-04-14",
    toDate: "2026-04-18",
    reason: "Vacation",
    status: "Pending",
    type: "Regular",
    totalDays: 5,
    appliedOn: "2026-04-10",
  },
];

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#D5D7DA", borderRadius: "10px" },
    "&:hover fieldset": { borderColor: "#4A7BA8" },
    "&.Mui-focused fieldset": { borderColor: "#244C77" },
  },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  } catch { return iso; }
}

const EMPTY_FORM = {
  fromDate: "",
  toDate: "",
  reason: "",
  description: "",
  type: "Regular",
};

function NewLeaveDialog({ open, onClose, onSuccess }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const totalDays = (() => {
    if (!form.fromDate || !form.toDate) return null;
    const from = new Date(form.fromDate);
    const to = new Date(form.toDate);
    if (to < from) return null;
    return Math.round((to - from) / (1000 * 60 * 60 * 24)) + 1;
  })();

  const handleChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors((p) => ({ ...p, [e.target.name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.fromDate) e.fromDate = "Required";
    if (!form.toDate) e.toDate = "Required";
    if (form.fromDate && form.toDate && new Date(form.fromDate) > new Date(form.toDate))
      e.toDate = "To Date must be after From Date";
    if (!form.reason) e.reason = "Required";
    return e;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      await postData("staff/leave", { ...form, totalDays });
      setToast({ open: true, severity: "success", message: "Leave request submitted." });
      setTimeout(() => {
        onSuccess && onSuccess();
        onClose();
        setForm(EMPTY_FORM);
        setErrors({});
      }, 900);
    } catch {
      setToast({ open: true, severity: "error", message: "Failed to submit. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <Dialog open={open} onClose={onClose} TransitionComponent={Transition} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: "bold", fontFamily: FONT }}>
          New Leave Request
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ fontSize: "13px", color: "#6B7280", mb: 0.5 }}>From Date *</FormLabel>
                <TextField size="small" name="fromDate" type="date" value={form.fromDate} onChange={handleChange} InputLabelProps={{ shrink: true }} error={!!errors.fromDate} helperText={errors.fromDate} sx={FIELD_SX} />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ fontSize: "13px", color: "#6B7280", mb: 0.5 }}>To Date *</FormLabel>
                <TextField size="small" name="toDate" type="date" value={form.toDate} onChange={handleChange} InputLabelProps={{ shrink: true }} error={!!errors.toDate} helperText={errors.toDate} sx={FIELD_SX} />
              </FormControl>
            </Grid>
            {totalDays !== null && (
              <Grid item xs={12}>
                <Chip label={`${totalDays} day${totalDays !== 1 ? "s" : ""}`} size="small" sx={{ backgroundColor: "#EEF2FF", color: "#244C77", fontWeight: 700 }} />
              </Grid>
            )}
            <Grid item xs={6}>
              <FormControl fullWidth error={!!errors.reason}>
                <FormLabel sx={{ fontSize: "13px", color: "#6B7280", mb: 0.5 }}>Reason *</FormLabel>
                <Select size="small" name="reason" value={form.reason} onChange={handleChange} sx={{ borderRadius: "10px", fontSize: "13px" }} displayEmpty>
                  <MenuItem value="" disabled><span style={{ color: "#9CA3AF" }}>Select reason</span></MenuItem>
                  {REASON_OPTIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
                {errors.reason && <Typography sx={{ fontSize: "11px", color: "#f44336", mt: 0.5 }}>{errors.reason}</Typography>}
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ fontSize: "13px", color: "#6B7280", mb: 0.5 }}>Type</FormLabel>
                <Select size="small" name="type" value={form.type} onChange={handleChange} sx={{ borderRadius: "10px", fontSize: "13px" }}>
                  <MenuItem value="Regular">Regular</MenuItem>
                  <MenuItem value="Mandatory">Mandatory</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <FormLabel sx={{ fontSize: "13px", color: "#6B7280", mb: 0.5 }}>Additional Notes</FormLabel>
                <TextField size="small" name="description" multiline rows={3} value={form.description} onChange={handleChange} placeholder="Optional details…" sx={FIELD_SX} />
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={onClose} sx={{ color: "#6B7280", textTransform: "none", border: "1px solid #D5D7DA", borderRadius: "8px", px: 3 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
            sx={{
              background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
              color: "#fff",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              fontWeight: 600,
              "&.Mui-disabled": { background: "#E5E7EB", color: "#9CA3AF" },
            }}
          >
            {submitting ? "Submitting…" : "Submit Request"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function StaffLeaveManagement() {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchData("staff/leave");
      const data = res?.data;
      if (Array.isArray(data) && data.length > 0) {
        setLeaves(data);
      } else if (data?.leaves && Array.isArray(data.leaves)) {
        setLeaves(data.leaves.length > 0 ? data.leaves : fallbackData);
      } else {
        setLeaves(fallbackData);
      }
    } catch {
      setLeaves(fallbackData);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLeaves();
  }, [loadLeaves]);

  const pendingCount = leaves.filter((l) => l.status === "Pending").length;
  const approvedCount = leaves.filter((l) => l.status === "Approved").length;
  const rejectedCount = leaves.filter((l) => l.status === "Rejected").length;

  return (
    <Box sx={{ padding: "1.5rem", backgroundColor: "#F8F9FA", minHeight: "100vh", fontFamily: FONT }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />

      <NewLeaveDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={() => {
          loadLeaves();
          setToast({ open: true, severity: "success", message: "Leave request submitted successfully." });
        }}
      />

      <Typography sx={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", mb: 0.5 }}>
        Pages / Leave Management
      </Typography>

      {/* Header row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: "22px", fontWeight: "bold", color: "#1a1a2e", fontFamily: FONT }}>
            Leave Management
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
            <Chip label={`${leaves.length} Total`} size="small" sx={{ backgroundColor: "#EEF2FF", color: "#244C77", fontWeight: 700, fontSize: "11px" }} />
            <Chip label={`${pendingCount} Pending`} size="small" sx={{ backgroundColor: "#fff3e0", color: "#ff9800", fontWeight: 700, fontSize: "11px" }} />
            <Chip label={`${approvedCount} Approved`} size="small" sx={{ backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: 700, fontSize: "11px" }} />
            <Chip label={`${rejectedCount} Rejected`} size="small" sx={{ backgroundColor: "#ffebee", color: "#f44336", fontWeight: 700, fontSize: "11px" }} />
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{
            background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
            color: "#fff",
            textTransform: "none",
            borderRadius: "10px",
            fontWeight: 600,
            px: 2.5,
            "&:hover": { background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)" },
          }}
        >
          New Leave Request
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: "#244C77" }} />
        </Box>
      ) : leaves.length === 0 ? (
        <Paper elevation={0} sx={{ p: 5, borderRadius: "16px", textAlign: "center", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
          <EventNoteIcon sx={{ fontSize: 48, color: "#D1D5DB", mb: 1.5 }} />
          <Typography sx={{ color: "#6B7280", fontSize: "15px" }}>No leave requests found.</Typography>
          <Button
            onClick={() => setDialogOpen(true)}
            sx={{ mt: 2, color: "#244C77", textTransform: "none", fontWeight: 600 }}
          >
            Submit your first leave request →
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {leaves.map((leave, i) => {
            const statusCfg = STATUS_CONFIG[leave.status] || { color: "#8F95B2", bg: "#F3F4F6" };
            const typeCfg = TYPE_CONFIG[leave.type] || TYPE_CONFIG.Regular;
            return (
              <Paper
                key={leave.id || i}
                elevation={0}
                sx={{
                  borderRadius: "14px",
                  p: 2.5,
                  boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                  border: "1px solid #F3F4F6",
                  "&:hover": { boxShadow: "0 4px 16px rgba(80,56,237,0.10)", borderColor: "#EAF1F8" },
                  transition: "all 0.15s",
                }}
              >
                <Grid container alignItems="center" spacing={1}>
                  {/* Left: date range & reason */}
                  <Grid item xs={12} sm={5}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <EventNoteIcon sx={{ fontSize: "16px", color: "#244C77" }} />
                      <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#1a1a2e", fontFamily: FONT }}>
                        {fmtDate(leave.fromDate)}
                        {leave.fromDate !== leave.toDate && (
                          <> &rarr; {fmtDate(leave.toDate)}</>
                        )}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                      {leave.reason}
                      {leave.totalDays ? ` · ${leave.totalDays} day${leave.totalDays !== 1 ? "s" : ""}` : ""}
                    </Typography>
                    {leave.appliedOn && (
                      <Typography sx={{ fontSize: "11px", color: "#9CA3AF", mt: 0.3 }}>
                        Applied: {fmtDate(leave.appliedOn)}
                      </Typography>
                    )}
                  </Grid>

                  {/* Middle: type */}
                  <Grid item xs={6} sm={3}>
                    <Chip
                      label={leave.type || "Regular"}
                      size="small"
                      sx={{
                        backgroundColor: typeCfg.bg,
                        color: typeCfg.color,
                        fontWeight: 700,
                        fontSize: "11px",
                        borderRadius: "20px",
                      }}
                    />
                  </Grid>

                  {/* Right: status */}
                  <Grid item xs={6} sm={4} sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
                    <Chip
                      label={leave.status}
                      size="small"
                      sx={{
                        backgroundColor: statusCfg.bg,
                        color: statusCfg.color,
                        fontWeight: 700,
                        fontSize: "12px",
                        borderRadius: "20px",
                        px: 0.5,
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
